import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  startAfter,
  type DocumentData,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import imageCompression from 'browser-image-compression';
import { db, storage } from '@/lib/firebase';

export type BillImage = {
  id: string;
  url: string;
  storagePath: string;
  serial: number;
  createdAt?: number;
  createdBy?: string | null;
};

const COLLECTION = 'handmade_bills';
export const BILLS_PAGE_SIZE = 30;

export type BillImagesPage = {
  bills: BillImage[];
  cursor: QueryDocumentSnapshot<DocumentData> | null;
};

export async function fetchBillImagesPage(
  cursor: QueryDocumentSnapshot<DocumentData> | null = null,
): Promise<BillImagesPage> {
  const constraints = cursor
    ? [orderBy('serial', 'asc'), startAfter(cursor), limit(BILLS_PAGE_SIZE)]
    : [orderBy('serial', 'asc'), limit(BILLS_PAGE_SIZE)];
  const snap = await getDocs(query(collection(db, COLLECTION), ...constraints));
  const bills = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<BillImage, 'id'>) }));
  const nextCursor = snap.docs.length === BILLS_PAGE_SIZE ? snap.docs[snap.docs.length - 1] : null;
  return { bills, cursor: nextCursor };
}

export async function uploadBillImage(file: File, uid: string | null): Promise<string> {
  // Only need the single highest serial, not the whole collection.
  const lastSnap = await getDocs(
    query(collection(db, COLLECTION), orderBy('serial', 'desc'), limit(1)),
  );
  const nextSerial = lastSnap.empty ? 1 : (lastSnap.docs[0].data().serial as number) + 1;

  // Compress in browser — max 500KB, 1200px
  const compressed = await imageCompression(file, {
    maxSizeMB: 0.5,
    maxWidthOrHeight: 1200,
    useWebWorker: true,
  });

  // Upload to Firebase Storage
  const ext = file.name.split('.').pop() ?? 'jpg';
  const storagePath = `handmade_bills/${nextSerial}_${Date.now()}.${ext}`;
  const storageRef = ref(storage, storagePath);
  await uploadBytes(storageRef, compressed, {
    contentType: compressed.type || 'image/jpeg',
  });
  const url = await getDownloadURL(storageRef);

  // Save metadata to Firestore
  const docRef = await addDoc(collection(db, COLLECTION), {
    url,
    storagePath,
    serial: nextSerial,
    createdAt: serverTimestamp(),
    createdBy: uid,
  });

  return docRef.id;
}

export async function deleteBillImage(id: string): Promise<void> {
  const docSnap = await getDoc(doc(db, COLLECTION, id));
  const storagePath = docSnap.exists() ? (docSnap.data().storagePath as string | undefined) : undefined;

  await deleteDoc(doc(db, COLLECTION, id));

  if (storagePath) {
    try {
      await deleteObject(ref(storage, storagePath));
    } catch {
      // already deleted, ignore
    }
  }
}
